import { FC, Fragment } from 'react';
import { useIntl } from 'react-intl';
import { isEmpty } from 'lodash';
import { createBemBlockBuilder, ShowMore } from '@ebsco-ui/ebsco-ui';

import {
  HoldingsStatement,
  SectionWithTitle,
  ShowMoreButton,
} from '@app/components';
import { wrapBoldTranslationChunks } from '@app/utils';
import { sharedMessages } from '@app/translations';

import {
  GroupedByLocationUniqueStatements,
  GroupedUniqueStatements,
} from './getGroupedByLocationHoldingStatements';
import { holdingNoteTypesMessages } from './holdingNoteTypesMessages';

import './LibraryHoldings.scss';

const cnBem = createBemBlockBuilder(['libraryHoldings']);

interface LibraryHoldingsProps {
  groupedHoldingStatements: GroupedByLocationUniqueStatements;
}

export const LibraryHoldings: FC<LibraryHoldingsProps> = ({
  groupedHoldingStatements,
}) => {
  const { $t } = useIntl();

  const getStatementNotesMessage = (statements: HoldingsStatement[]) => {
    const note = statements
      .map(({ note }) => note)
      .filter(Boolean)
      .join(', ');

    return note ? (
      <div data-auto="statement-notes" className={cnBem('__statementNote')}>
        {$t(
          {
            id: 'instanceDetails.notes',
            defaultMessage: '<b>Notes:</b> {note}',
          },
          { b: wrapBoldTranslationChunks, note }
        )}{' '}
      </div>
    ) : null;
  };

  const getHoldingNotesMessages = ({ notes = [] }: GroupedUniqueStatements) => {
    if (isEmpty(notes)) {
      return null;
    }

    return notes.map(({ holdingsNoteTypeName, note }) => {
      if (!holdingsNoteTypeName || !note) {
        return null;
      }

      const noteType = holdingsNoteTypeName.toLowerCase();
      const messageKey = holdingNoteTypesMessages[noteType];

      return (
        <div data-auto="holding-notes" key={`${holdingsNoteTypeName}-${note}`}>
          {messageKey ? (
            $t(messageKey, { b: wrapBoldTranslationChunks, note })
          ) : (
            <>
              <b>{`${holdingsNoteTypeName}:`}</b> {note}
            </>
          )}
        </div>
      );
    });
  };

  const sections = [
    {
      title: $t({
        id: 'instanceDetails.libraryHas',
        defaultMessage: 'Library has:',
      }),
      key: 'holdingsStatements',
    },
    {
      title: $t({
        id: 'instanceDetails.holdingsSupplements',
        defaultMessage: 'Holdings (supplements):',
      }),
      key: 'holdingsStatementsForSupplements',
    },
    {
      title: $t({
        id: 'instanceDetails.holdingsIndexes',
        defaultMessage: 'Holdings (indexes):',
      }),
      key: 'holdingsStatementsForIndexes',
    },
  ];

  const renderSection = (title: string, statements: HoldingsStatement[]) => {
    if (isEmpty(statements)) {
      return null;
    }

    const listLimit = 3;

    const listElements = statements
      .filter(({ statement }) => statement)
      .map(({ statement }) => <li key={statement}>{statement}</li>);

    return (
      <div data-auto="location-statement">
        <h4 data-auto="statement-name">{title}</h4>
        <ShowMore limit={listLimit}>
          <ul>
            <ShowMore.Items>{listElements}</ShowMore.Items>
          </ul>
          <ShowMoreButton
            showMoreLimit={listLimit}
            dataLength={listElements.length}
          />
        </ShowMore>
        {getStatementNotesMessage(statements)}
      </div>
    );
  };

  const renderStatements = () =>
    Object.entries(groupedHoldingStatements).map(([location, statements]) => (
      <div
        className={cnBem('__row')}
        key={location}
        data-auto="location-container"
      >
        <h3 data-auto="location-name">{location}</h3>
        {sections.map(({ title, key }) => (
          <Fragment key={key}>{renderSection(title, statements[key])}</Fragment>
        ))}
        <div className={cnBem('__holdingNotes')}>
          {getHoldingNotesMessages(statements)}
        </div>
      </div>
    ));

  return (
    <div className={cnBem()} data-auto="library-holdings">
      <SectionWithTitle title={$t(sharedMessages.libraryHoldings)}>
        {renderStatements()}
      </SectionWithTitle>
    </div>
  );
};
